import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AnalyticsContext } from "../context/analyticsContext";
import { ISearchResults } from "../interface/iSearchResults";
import { IAppointment } from "../interface/iAppointment";

interface ILocationRow {
  location: string;
  participantCount: number;
  participants: string[];
}

const LocationAnalysis = (): React.JSX.Element => {
  const { searchResults }: { searchResults: ISearchResults } =
    React.useContext(AnalyticsContext);

  const rows = React.useMemo((): ILocationRow[] => {
    const appointments = searchResults.appointmentList?.appointments ?? [];

    const relevant = appointments.filter(
      (a: IAppointment) =>
        (a.status === "G" || a.status === "V") && a.email && a.location,
    );

    const byLocation = new Map<string, Map<string, string>>();
    relevant.forEach((a: IAppointment) => {
      const loc = a.location as string;
      if (!byLocation.has(loc)) byLocation.set(loc, new Map());
      const fullName =
        [a.firstName, a.lastName].filter(Boolean).join(" ") || a.email;
      byLocation.get(loc)!.set(a.email, fullName);
    });

    return Array.from(byLocation.entries())
      .map(([location, participantMap]) => ({
        location,
        participantCount: participantMap.size,
        participants: Array.from(participantMap.values()).sort(),
      }))
      .sort((a, b) => b.participantCount - a.participantCount);
  }, [searchResults]);

  if (rows.length === 0) return <></>;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Auswertung Standorte
      </Typography>

      <Box sx={{ mt: 2 }}>
        {rows.map((row) => (
          <Accordion
            key={row.location}
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "divider",
              "&:not(:last-child)": { borderBottom: 0 },
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ flex: 1 }}>{row.location}</Typography>
              <Chip
                label={`${row.participantCount} Teilnehmer`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {row.participants.map((name) => {
                  const initials = name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2);
                  return (
                    <Chip
                      key={name}
                      avatar={<Avatar>{initials}</Avatar>}
                      label={name}
                      size="small"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default LocationAnalysis;
